import { useState, useEffect } from 'react';
import { Modal, Badge } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import DataTable from 'react-data-table-component';

import { limpiarVenta, obtenerVenta } from '../../../actions/ventas';

import { formatNumber, printTicket } from '../../../utils/generalFunctions';

const DetallesVenta = ({ venta, reestablecerVenta }) => {

    const dispatch =  useDispatch();

    const { detalles } = useSelector(state => state.ventas);

    const [detallesModal, setDetallesModal] = useState(false);

    const [ventaData, setVentaData] = useState({
        id: null,
        folio: null,
        cliente: null,
        metodos: [],
        fecha: null,
        status: 0,
        total: 0,
        productos: [],
        servicios: [],
        medicos: [],
        medicos_externos: [],
        observaciones: null
    });

    const [tabActivo, setTabActivo] = useState(null);

    const columnsProductos = [
        {
            name: 'Producto',
            cell: row => row.producto?.nombre || 'Sin definir',
            grow: 2
        },
        {
            name: 'Paciente',
            cell: row => row.paciente?.nombre || 'Sin definir'
        },
        {
            name: 'Cantidad',
            cell: row => formatNumber(row.cantidad, 2, '', '')
        }, 
        {
            name: 'Unidad',
            cell: row => row.unidad?.nombre || 'Sin definir'
        }, 
        {
            name: 'Precio unitario',
            cell: row => formatNumber((row.precio != null && row.precio != undefined ? parseFloat(row.precio) : parseFloat(row.producto?.precio_venta)) / row.conversion, 2, '$ ', '')
        },
        {
            name: 'Importe',
            cell: row => formatNumber(parseFloat(row.cantidad) * (row.precio != null && row.precio != undefined ? parseFloat(row.precio) : parseFloat(row.producto?.precio_venta)) / row.conversion, 2, '$ ', '')
        }
    ];

    const columnsServicios = [
        {
            name: 'Servicio',
            cell: row => row.servicio?.nombre || 'Sin definir',
            grow: 3
        },
        {
            name: 'Paciente',
            cell: row => row.paciente?.nombre || 'Sin definir'
        },
        {
            name: 'Cantidad',
            cell: row => formatNumber(row.cantidad || 1, 0, '', '')
        },
        {
            name: 'Precio unitario',
            cell: row => formatNumber(row.precio, 2, '$ ', '')
        },
        {
            name: 'Importe',
            cell: row => formatNumber((row.cantidad || 1) * parseFloat(row.precio || 0), 2, '$ ', '')
        }
    ];

    const columnsMedicos = [
        {
            name: 'Nombre',
            cell: row => row.medico?.nombre || 'Sin definir',
            grow: 2
        },
        {
            name: 'Apellido',
            cell: row => row.medico?.apellido || 'Sin definir',
            grow: 2
        },
        {
            name: 'Tipo de colaborador',
            cell: row => row.medico?.tipo || 'Sin definir'
        }
    ];

    const columnsMedicosExternos = [
        {
            name: 'Nombre',
            cell: row => row.medico_externo?.nombre || 'Sin definir',
            grow: 3
        },
        {
            name: 'Veterinaria',
            cell: row => row.medico_externo?.veterinaria || 'Sin definir',
            grow: 2
        }
    ];

    /* Detalles venta */
    const handleAbrir = async () => {
        await dispatch(limpiarVenta());

        await dispatch(obtenerVenta(venta?.id, true));
    }

    const handleImprimirVenta = () => {
        printTicket(ventaData);
    }

    const handleCerrar = () => {
        setVentaData({
            id: null,
            concepto: '', 
            id_proveedor: null,  
            id_metodo: null,  
            monto: 0
        });

        setDetallesModal(false);
        reestablecerVenta(null);

        dispatch(limpiarVenta());
    }

    useEffect(() => {
        if(venta){
            setDetallesModal(true);

            dispatch(obtenerVenta(venta?.id, true));
        }        
    }, [venta]);

    useEffect(() => {
        if(detalles){
            setVentaData({
                ...ventaData,
                id: detalles?.id,
                folio: detalles?.folio,
                cliente: detalles?.cliente,
                metodos: detalles?.metodos || [],
                fecha: detalles?.fecha,
                status: detalles?.status,
                total: detalles?.total,
                productos: detalles?.productos,
                servicios: detalles?.servicios,
                medicos: detalles?.medicos,
                medicos_externos: detalles?.medicos_externos,
                observaciones: detalles?.observaciones || null
            });

            if(detalles?.productos?.length){
                setTabActivo('productos');
            }else if(detalles?.servicios?.length){
                setTabActivo('servicios');
            }else if(detalles?.medicos?.length){
                setTabActivo('medicos');
            }else if(detalles?.medicos_externos?.length){
                setTabActivo('medicos_externos');
            }else{
                setTabActivo(null);
            }
        }
    }, [detalles]);

    return (
        <Modal show={detallesModal} size="xl" backdrop="static" centered onEntered={() => handleAbrir()} onHide={() => handleCerrar()}>
            <Modal.Header closeButton>
                <Modal.Title>Información de la venta {ventaData.folio ? `#${ventaData.folio}` : ''}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row">
                    <div className="col-md-3">
                        <label className="fw-bold">Cliente</label>
                        <p>{ventaData?.cliente?.nombre ? ventaData?.cliente?.nombre + ' ' + ventaData?.cliente?.apellido : 'Público general'}</p>
                    </div>

                    <div className="col-md-4">
                        <label className="fw-bold">Método de pago</label>
                        <p>
                            {
                                ventaData?.metodos?.length > 0
                                    ? (() => {
                                        const nombres = ventaData.metodos
                                            .map(m => m.metodo?.nombre)
                                            .filter(Boolean);

                                        if (nombres.length === 1) {
                                            return nombres[0];
                                        }

                                        if (nombres.length === 2) {
                                            return `${nombres[0]} y ${nombres[1]}`;
                                        }

                                        return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
                                    })()
                                    : 'Sin definir'
                            }
                        </p>
                    </div>

                    <div className="col-md-3">
                        <label className="fw-bold">Fecha</label>
                        <p>{ventaData?.fecha && ventaData?.fecha != '0000-00-00' ? new Date(ventaData?.fecha).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) + ' ' + new Date(ventaData?.fecha).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : 'Sin definir'}</p>
                    </div>

                    <div className="col-md-2">
                        <label className="fw-bold">Status</label>
                        <p><h5><Badge pill bg={ventaData?.status ? (ventaData?.status == 1 ? 'danger' : 'info') : 'success'}>{ventaData?.status ? (ventaData?.status == 1 ? 'Cancelada' : 'Pausada') : 'Realizada'}</Badge></h5></p>
                    </div>

                    <div className="col-md-12">
                        <label className="fw-bold">Observaciones</label>
                        <p>{ventaData?.observaciones || 'Sin observaciones'}</p>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-2 offset-md-10">
                        <label className="fw-bold">Total</label>
                        <p>{formatNumber((ventaData?.total || 0), 2, '$ ', '')}</p>
                    </div>
                </div>

                <ul className="nav nav-tabs mb-3 custom-tabs">
                    {ventaData.productos?.length > 0 && (
                        <li className="nav-item">
                            <button className={`nav-link ${tabActivo === 'productos' ? 'active' : ''}`} onClick={() => setTabActivo('productos')} type="button">Productos <span className="badge bg-secondary ms-2"> {ventaData.productos.length} </span></button>
                        </li>
                    )}

                    {ventaData.servicios?.length > 0 && (
                        <li className="nav-item">
                            <button className={`nav-link ${tabActivo === 'servicios' ? 'active' : ''}`} onClick={() => setTabActivo('servicios')} type="button">Servicios <span className="badge bg-secondary ms-2"> {ventaData.servicios.length} </span></button>
                        </li>
                    )}

                    {ventaData.medicos?.length > 0 && (
                        <li className="nav-item">
                            <button className={`nav-link ${tabActivo === 'medicos' ? 'active' : ''}`} onClick={() => setTabActivo('medicos')} type="button">Médicos <span className="badge bg-secondary ms-2"> {ventaData.medicos.length} </span></button>
                        </li>
                    )}

                    {ventaData.medicos_externos?.length > 0 && (
                        <li className="nav-item">
                            <button className={`nav-link ${tabActivo === 'medicos_externos' ? 'active' : ''}`} onClick={() => setTabActivo('medicos_externos')} type="button">Médicos externos <span className="badge bg-secondary ms-2"> {ventaData.medicos_externos.length} </span></button>
                        </li>
                    )}
                </ul>

                <div className="tab-content">
                    {tabActivo === 'productos' && (
                        <div className="tab-pane active">
                            <DataTable
                                columns={columnsProductos}
                                data={ventaData.productos}
                                highlightOnHover
                            />
                        </div>
                    )}

                    {tabActivo === 'servicios' && (
                        <div className="tab-pane active">
                            <DataTable
                                columns={columnsServicios}
                                data={ventaData.servicios}
                                highlightOnHover
                            />
                        </div>
                    )}

                    {tabActivo === 'medicos' && (
                        <div className="tab-pane active">
                            <DataTable
                                columns={columnsMedicos}
                                data={ventaData.medicos}
                                highlightOnHover
                            />
                        </div>
                    )}

                    {tabActivo === 'medicos_externos' && (
                        <div className="tab-pane active">
                            <DataTable
                                columns={columnsMedicosExternos}
                                data={ventaData.medicos_externos}
                                highlightOnHover
                            />
                        </div>
                    )}
                </div>
            </Modal.Body>
            {
                ventaData?.status == 0 &&
                    <Modal.Footer>
                        <button type="button" className="btn btn-warning" onClick={() => handleImprimirVenta()}><i className="fas fa-receipt fa-sm"></i> Imprimir ticket</button>
                    </Modal.Footer>
            }
        </Modal>
    )
}

export default DetallesVenta;